import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminShell } from "@/components/admin/AdminShell";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle, ChevronDown, ChevronRight, Download } from "lucide-react";
import { toCsv, downloadCsv } from "@/lib/csv";

type Run = {
  id: string;
  started_at: string;
  finished_at: string | null;
  ok: boolean;
  total: number;
  passed: number;
  failed: number;
  cleaned_up: boolean;
  utm: any;
  checks: { name: string; ok: boolean; detail?: string }[];
  row_counts: any;
  error: string | null;
};

export default function AdminAttributionTestHistory() {
  const [rows, setRows] = useState<Run[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<Set<string>>(new Set());

  async function load() {
    setLoading(true);
    const { data } = await supabase
      .from("attribution_test_runs" as any)
      .select("*").order("started_at", { ascending: false }).limit(200);
    setRows((data as any) ?? []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  const totals = rows.reduce((acc, r) => {
    acc.runs++; acc.checks += r.total; acc.passed += r.passed; acc.failed += r.failed;
    if (r.ok) acc.green++; else acc.red++;
    return acc;
  }, { runs: 0, checks: 0, passed: 0, failed: 0, green: 0, red: 0 });

  function exportCsv() {
    const flat = rows.flatMap(r => r.checks.map((c, i) => ({
      run_id: r.id,
      started_at: r.started_at,
      ok: r.ok,
      total: r.total,
      passed: r.passed,
      failed: r.failed,
      cleaned_up: r.cleaned_up,
      utm_campaign: r.utm?.utm_campaign ?? "",
      utm_source: r.utm?.utm_source ?? "",
      views: r.row_counts?.views ?? "",
      redemptions: r.row_counts?.redemptions ?? "",
      conversion_rate_pct: r.row_counts?.conversion_rate_pct ?? "",
      check_index: i,
      check_name: c.name,
      check_ok: c.ok,
      check_detail: c.detail ?? "",
    })));
    downloadCsv("attribution-test-runs.csv", toCsv(flat));
  }

  return (
    <AdminShell title="Attribution Test Results" actions={
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={exportCsv} disabled={rows.length === 0}><Download className="w-4 h-4 mr-1" /> CSV</Button>
        <Link to="/admin/attribution-test" className="text-xs underline self-center">← Run new test</Link>
      </div>
    }>
      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
            <Stat label="Runs" v={totals.runs} />
            <Stat label="Pass" v={totals.green} tone="text-emerald-500" />
            <Stat label="Fail" v={totals.red} tone="text-destructive" />
            <Stat label="Total assertions" v={totals.checks} />
            <Stat label="Pass rate" v={totals.checks ? `${((totals.passed / totals.checks) * 100).toFixed(1)}%` : "—"} />
          </div>

          <div className="bg-surface border border-border">
            {rows.length === 0 ? <div className="p-6 text-center text-muted-foreground">No test runs recorded yet.</div> : (
              <table className="w-full text-sm">
                <thead className="bg-panel">
                  <tr>
                    <th className="w-6"></th>
                    <th className="text-left p-2">Started</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Passed</th>
                    <th className="text-left p-2">UTM campaign</th>
                    <th className="text-left p-2">Views / Redemptions / CR</th>
                    <th className="text-left p-2">Cleaned</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => {
                    const isOpen = open.has(r.id);
                    const rc = r.row_counts ?? {};
                    return (
                      <>
                        <tr key={r.id} className="border-t border-border hover:bg-panel/40 cursor-pointer" onClick={() => {
                          const n = new Set(open); n.has(r.id) ? n.delete(r.id) : n.add(r.id); setOpen(n);
                        }}>
                          <td className="p-2">{isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
                          <td className="p-2 font-mono text-xs">{new Date(r.started_at).toLocaleString()}</td>
                          <td className="p-2">
                            <Badge variant={r.ok ? "default" : "destructive"} className="gap-1">
                              {r.ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                              {r.ok ? "pass" : "fail"}
                            </Badge>
                          </td>
                          <td className="p-2">{r.passed}/{r.total}</td>
                          <td className="p-2 font-mono text-xs">{r.utm?.utm_campaign ?? "—"}</td>
                          <td className="p-2 font-mono text-xs">{rc.views ?? "—"} / {rc.redemptions ?? "—"} / {rc.conversion_rate_pct ?? "—"}%</td>
                          <td className="p-2 text-xs">{r.cleaned_up ? "yes" : "no"}</td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-panel/30">
                            <td colSpan={7} className="p-3">
                              <ul className="space-y-1">
                                {r.checks.map((c, i) => (
                                  <li key={i} className="flex items-start gap-2 text-xs">
                                    {c.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5" /> : <XCircle className="w-3.5 h-3.5 text-destructive mt-0.5" />}
                                    <div>
                                      <div>{c.name}</div>
                                      {c.detail && <div className="text-[11px] text-muted-foreground font-mono break-all">{c.detail}</div>}
                                    </div>
                                  </li>
                                ))}
                              </ul>
                              <details className="mt-3 text-[11px]">
                                <summary className="cursor-pointer text-muted-foreground">UTM tuple</summary>
                                <pre className="font-mono mt-1 overflow-x-auto">{JSON.stringify(r.utm, null, 2)}</pre>
                              </details>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </AdminShell>
  );
}

function Stat({ label, v, tone }: { label: string; v: any; tone?: string }) {
  return (
    <div className="bg-surface border border-border p-3">
      <div className="uppercase tracking-widest text-[10px] text-muted-foreground">{label}</div>
      <div className={`text-xl font-display ${tone ?? ""}`}>{v}</div>
    </div>
  );
}